import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { z } from 'zod';
import {
  GAME_SESSION_RECORD_VERSION,
  type GameSessionRecord,
} from '../session';

export const GAME_SESSION_PERSISTENCE_DATABASE = 'capychess-game-sessions';
export const GAME_SESSION_PERSISTENCE_DATABASE_VERSION = 1;

const CURRENT_STORE = 'current-session';
const COMPLETED_STORE = 'completed-sessions';
const CURRENT_SESSION_KEY = 'current';
const CURRENT_SESSION_ENVELOPE_VERSION = 1;

const UCI_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

const timestampSchema = z.number().refine(
  (value) => Number.isSafeInteger(value) && value >= 0,
  'Expected a non-negative safe integer timestamp.',
);

const positiveSafeIntegerSchema = z.number().refine(
  (value) => Number.isSafeInteger(value) && value > 0,
  'Expected a positive safe integer.',
);

const nonNegativeSafeIntegerSchema = z.number().refine(
  (value) => Number.isSafeInteger(value) && value >= 0,
  'Expected a non-negative safe integer.',
);

const colorSchema = z.enum(['w', 'b']);
const uciMoveSchema = z.string().regex(UCI_PATTERN, 'Expected a lowercase UCI move.');

const clockSchema = z.object({
  initialMs: positiveSafeIntegerSchema,
  incrementMs: nonNegativeSafeIntegerSchema,
  whiteRemainingMs: timestampSchema,
  blackRemainingMs: timestampSchema,
  activeColor: colorSchema,
  activeDeadlineMs: timestampSchema,
}).strict();

const acceptedMoveSchema = z.object({
  uci: uciMoveSchema,
  san: z.string(),
  actor: z.enum(['hot-seat', 'human', 'computer']),
  committedAtMs: timestampSchema,
  clockBefore: clockSchema.optional(),
}).strict();

const playerModeSchema = z.union([
  z.object({ kind: z.literal('hot-seat') }).strict(),
  z.object({
    kind: z.literal('computer'),
    humanColor: colorSchema,
    humanColorPreference: z.enum(['w', 'b', 'random']).optional(),
    learnMode: z.boolean(),
    ai: z.object({
      id: z.string().min(1),
      tierVersion: z.string().min(1),
      engineBuild: z.string().min(1),
      gameSeed: z.string().min(1),
    }).strict(),
  }).strict(),
]);

const sessionResultSchema = z.union([
  z.object({
    terminal: z.literal(false),
    token: z.literal('*'),
    reason: z.literal('ongoing'),
  }).strict(),
  z.object({
    terminal: z.literal(true),
    token: z.enum(['1-0', '0-1']),
    reason: z.enum(['timeout', 'checkmate', 'resignation']),
    winner: colorSchema,
  }).strict().superRefine((result, context) => {
    const expectedToken = result.winner === 'w' ? '1-0' : '0-1';
    if (result.token !== expectedToken) {
      context.addIssue({
        code: 'custom',
        path: ['token'],
        message: 'A winning token must match the winner.',
      });
    }
  }),
  z.object({
    terminal: z.literal(true),
    token: z.literal('1/2-1/2'),
    reason: z.enum([
      'stalemate',
      'threefold-repetition',
      'fifty-move-rule',
      'insufficient-material',
      'draw',
    ]),
  }).strict(),
]);

const aiRequestSchema = z.object({
  requestId: positiveSafeIntegerSchema,
  gameEpoch: nonNegativeSafeIntegerSchema,
  positionRevision: nonNegativeSafeIntegerSchema,
  positionFen: z.string(),
  ply: nonNegativeSafeIntegerSchema,
  profileId: z.string(),
  tierVersion: z.string(),
  engineBuild: z.string(),
  gameSeed: z.string(),
}).strict();

const aiFailureSchema = z.object({
  code: z.enum(['empty-result', 'invalid-uci', 'illegal-move']),
  requestId: positiveSafeIntegerSchema,
}).strict();

const aiStateSchema = z.object({
  nextRequestId: positiveSafeIntegerSchema,
  pending: aiRequestSchema.optional(),
  lastFailure: aiFailureSchema.optional(),
}).strict();

const clockTimelineAnchorSchema = z.object({
  ply: nonNegativeSafeIntegerSchema,
  atMs: timestampSchema,
  clock: clockSchema,
}).strict();

/**
 * The structural storage boundary for the session domain's current record
 * version. Stored IndexedDB values are always parsed through this schema
 * before the app can attempt a session restore.
 */
export const GameSessionRecordSchema = z.object({
  schemaVersion: z.literal(GAME_SESSION_RECORD_VERSION),
  gameId: z.string().min(1),
  createdAtMs: timestampSchema,
  updatedAtMs: timestampSchema,
  gameEpoch: nonNegativeSafeIntegerSchema,
  initialFen: z.string(),
  acceptedUciMoves: z.array(uciMoveSchema),
  acceptedMoves: z.array(acceptedMoveSchema),
  derivedFen: z.string(),
  result: sessionResultSchema,
  mode: playerModeSchema,
  clock: clockSchema.optional(),
  clockTimelineAnchors: z.array(clockTimelineAnchorSchema).optional(),
  ai: aiStateSchema,
}).strict().superRefine((record, context) => {
  if (record.updatedAtMs < record.createdAtMs) {
    context.addIssue({
      code: 'custom',
      path: ['updatedAtMs'],
      message: 'A session cannot be updated before it was created.',
    });
  }

  if (record.acceptedMoves.length !== record.acceptedUciMoves.length) {
    context.addIssue({
      code: 'custom',
      path: ['acceptedMoves'],
      message: 'Accepted move metadata must match the UCI history length.',
    });
    return;
  }

  for (const [index, move] of record.acceptedMoves.entries()) {
    if (move.uci !== record.acceptedUciMoves[index]) {
      context.addIssue({
        code: 'custom',
        path: ['acceptedMoves', index, 'uci'],
        message: 'Accepted move metadata must match the UCI history.',
      });
    }
  }
});

/**
 * The current-slot envelope is intentionally independent of the session
 * record version. Raw v1 records remain readable, while new writes carry a
 * durable generation even after a terminal game is archived.
 */
const currentSessionEnvelopeSchema = z.object({
  envelopeVersion: z.literal(CURRENT_SESSION_ENVELOPE_VERSION),
  generation: z.number().refine(
    (value) => Number.isSafeInteger(value) && value > 0 && value < Number.MAX_SAFE_INTEGER,
    'Expected a generation that can be incremented safely.',
  ),
  record: GameSessionRecordSchema.optional(),
}).strict();

type CurrentSessionExpectation =
  | { readonly kind: 'empty' }
  | { readonly kind: 'generation'; readonly generation: number }
  | { readonly kind: 'legacy'; readonly fingerprint: string }
  | { readonly kind: 'unavailable' };

type ParsedCurrentSessionValue =
  | {
      readonly kind: 'empty';
      readonly expectation: Extract<CurrentSessionExpectation, { readonly kind: 'empty' }>;
    }
  | {
      readonly kind: 'generation';
      readonly expectation: Extract<CurrentSessionExpectation, { readonly kind: 'generation' }>;
      readonly generation: number;
      readonly record?: GameSessionRecord;
    }
  | {
      readonly kind: 'legacy';
      readonly expectation: Extract<CurrentSessionExpectation, { readonly kind: 'legacy' }>;
      readonly record: GameSessionRecord;
    }
  | { readonly kind: 'invalid' };

interface GameSessionPersistenceDatabase extends DBSchema {
  [CURRENT_STORE]: {
    key: typeof CURRENT_SESSION_KEY;
    value: unknown;
  };
  [COMPLETED_STORE]: {
    key: string;
    value: unknown;
    indexes: { 'by-updated-at': number };
  };
}

export interface PersistedGameSessions {
  readonly current?: GameSessionRecord;
  /** A terminal record stranded in the current store after interrupted archival. */
  readonly recoverableTerminal?: GameSessionRecord;
  readonly completed: readonly GameSessionRecord[];
}

export type GameSessionPersistenceFailure =
  | {
      readonly kind: 'invalid-record';
      readonly location: 'current' | 'completed';
    }
  | {
      /** Another tab changed the current slot after this instance last read it. */
      readonly kind: 'conflict';
      readonly operation: 'write';
    }
  | {
      readonly kind: 'quota';
      readonly operation: 'read' | 'write';
      readonly cause: unknown;
    }
  | {
      readonly kind: 'security';
      readonly operation: 'read' | 'write';
      readonly cause: unknown;
    }
  | {
      readonly kind: 'read-failed';
      readonly cause: unknown;
    }
  | {
      readonly kind: 'write-failed';
      readonly cause: unknown;
    };

export type ReadPersistedGameSessionsResult =
  | { readonly ok: true; readonly value: PersistedGameSessions }
  | {
      readonly ok: false;
      readonly error: GameSessionPersistenceFailure;
      /** A current record is retained only after it has passed Zod validation. */
      readonly fallback: PersistedGameSessions;
    };

export type SaveGameSessionResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: GameSessionPersistenceFailure };

export interface IndexedDbGameSessionPersistenceOptions {
  readonly databaseName?: string;
}

/**
 * Browser storage adapter for one resumable game plus an archive of completed
 * games. Failed, corrupt, or version-mismatched values never become a current
 * game, and all mutating archive operations are one IndexedDB transaction.
 */
export class IndexedDbGameSessionPersistence {
  private readonly databaseName: string;
  private databasePromise: Promise<IDBPDatabase<GameSessionPersistenceDatabase>> | undefined;
  private lastValidCurrent: GameSessionRecord | undefined;
  /** Expected current-slot state for this tab; never advance it after a conflict. */
  private expectedCurrent: CurrentSessionExpectation = { kind: 'empty' };

  public constructor(options: IndexedDbGameSessionPersistenceOptions = {}) {
    this.databaseName = options.databaseName ?? GAME_SESSION_PERSISTENCE_DATABASE;
  }

  /** Reads the resumable game and every valid completed-game record. */
  public async read(): Promise<ReadPersistedGameSessionsResult> {
    let currentValue: unknown;
    let completedValues: unknown[];
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction([CURRENT_STORE, COMPLETED_STORE], 'readonly');
      const currentStore = transaction.objectStore(CURRENT_STORE);
      const completedStore = transaction.objectStore(COMPLETED_STORE);
      [currentValue, completedValues] = await Promise.all([
        currentStore.get(CURRENT_SESSION_KEY),
        completedStore.getAll(),
        transaction.done,
      ]);
    } catch (cause) {
      this.expectedCurrent = { kind: 'unavailable' };
      return {
        ok: false,
        error: toStorageFailure('read', cause),
        fallback: this.snapshot(this.lastValidCurrent, []),
      };
    }

    const parsedCurrent = parseCurrentSessionValue(currentValue);
    this.expectedCurrent = parsedCurrent.kind === 'invalid'
      ? { kind: 'unavailable' }
      : parsedCurrent.expectation;
    const current = parsedCurrent.kind === 'generation' || parsedCurrent.kind === 'legacy'
      ? parsedCurrent.record
      : undefined;
    const completed = completedValues
      .map(parseRecord)
      .filter((record): record is GameSessionRecord => record !== undefined)
      .filter((record) => record.result.terminal)
      .sort(compareByMostRecentUpdate);
    const strandedTerminal = current?.result.terminal ? current : undefined;

    const currentIsValid = current !== undefined && !current.result.terminal;
    if (currentIsValid) {
      this.lastValidCurrent = current;
    } else if (parsedCurrent.kind === 'empty' || (
      parsedCurrent.kind === 'generation' && parsedCurrent.record === undefined
    )) {
      this.lastValidCurrent = undefined;
    }

    const snapshot = this.snapshot(
      currentIsValid ? current : this.lastValidCurrent,
      completed,
      strandedTerminal,
    );

    if (parsedCurrent.kind === 'invalid' || (current !== undefined && !currentIsValid)) {
      return {
        ok: false,
        error: { kind: 'invalid-record', location: 'current' },
        fallback: snapshot,
      };
    }

    if (completed.length !== completedValues.length) {
      return {
        ok: false,
        error: { kind: 'invalid-record', location: 'completed' },
        fallback: snapshot,
      };
    }

    return { ok: true, value: snapshot };
  }

  /**
   * Saves an active game as the sole resumable session. A terminal record is
   * archived and replaces the active record with a generation tombstone in
   * the same transaction.
   */
  public async save(record: GameSessionRecord): Promise<SaveGameSessionResult> {
    const validated = parseRecord(record);
    if (validated === undefined) {
      return { ok: false, error: { kind: 'invalid-record', location: 'current' } };
    }
    if (this.expectedCurrent.kind === 'unavailable') {
      return { ok: false, error: { kind: 'conflict', operation: 'write' } };
    }

    try {
      const database = await this.getDatabase();
      const transaction = database.transaction([CURRENT_STORE, COMPLETED_STORE], 'readwrite');
      const currentStore = transaction.objectStore(CURRENT_STORE);
      const completedStore = transaction.objectStore(COMPLETED_STORE);
      const actualCurrent = parseCurrentSessionValue(await currentStore.get(CURRENT_SESSION_KEY));
      if (!currentMatchesExpectation(actualCurrent, this.expectedCurrent)) {
        await transaction.done;
        return { ok: false, error: { kind: 'conflict', operation: 'write' } };
      }

      const nextGeneration = nextGenerationFor(actualCurrent);
      if (validated.result.terminal) {
        await completedStore.put(validated);
        await currentStore.put({
          envelopeVersion: CURRENT_SESSION_ENVELOPE_VERSION,
          generation: nextGeneration,
        }, CURRENT_SESSION_KEY);
      } else {
        await currentStore.put({
          envelopeVersion: CURRENT_SESSION_ENVELOPE_VERSION,
          generation: nextGeneration,
          record: validated,
        }, CURRENT_SESSION_KEY);
      }
      await transaction.done;
      this.expectedCurrent = { kind: 'generation', generation: nextGeneration };
      this.lastValidCurrent = validated.result.terminal ? undefined : validated;
      return { ok: true };
    } catch (cause) {
      return { ok: false, error: toStorageFailure('write', cause) };
    }
  }

  /** Releases the open database connection when an owning app is disposed. */
  public close(): void {
    this.databasePromise?.then((database) => database.close()).catch(() => undefined);
    this.databasePromise = undefined;
  }

  private getDatabase(): Promise<IDBPDatabase<GameSessionPersistenceDatabase>> {
    if (this.databasePromise === undefined) {
      const opening = openDB<GameSessionPersistenceDatabase>(
        this.databaseName,
        GAME_SESSION_PERSISTENCE_DATABASE_VERSION,
        {
          upgrade(database) {
            if (!database.objectStoreNames.contains(CURRENT_STORE)) {
              database.createObjectStore(CURRENT_STORE);
            }
            if (!database.objectStoreNames.contains(COMPLETED_STORE)) {
              const completed = database.createObjectStore(COMPLETED_STORE, { keyPath: 'gameId' });
              completed.createIndex('by-updated-at', 'updatedAtMs');
            }
          },
        },
      );
      this.databasePromise = opening;
      void opening.catch(() => {
        if (this.databasePromise === opening) {
          this.databasePromise = undefined;
        }
      });
    }
    return this.databasePromise;
  }

  private snapshot(
    current: GameSessionRecord | undefined,
    completed: readonly GameSessionRecord[],
    recoverableTerminal?: GameSessionRecord,
  ): PersistedGameSessions {
    return Object.freeze({
      ...(current ? { current: copyRecord(current) } : {}),
      ...(recoverableTerminal ? { recoverableTerminal: copyRecord(recoverableTerminal) } : {}),
      completed: Object.freeze(completed.map(copyRecord)),
    });
  }
}

export function createIndexedDbGameSessionPersistence(
  options?: IndexedDbGameSessionPersistenceOptions,
): IndexedDbGameSessionPersistence {
  return new IndexedDbGameSessionPersistence(options);
}

function parseRecord(value: unknown): GameSessionRecord | undefined {
  const parsed = GameSessionRecordSchema.safeParse(value);
  return parsed.success ? copyRecord(parsed.data) : undefined;
}

function parseCurrentSessionValue(value: unknown): ParsedCurrentSessionValue {
  if (value === undefined) {
    return { kind: 'empty', expectation: { kind: 'empty' } };
  }

  const envelope = currentSessionEnvelopeSchema.safeParse(value);
  if (envelope.success) {
    const record = envelope.data.record === undefined ? undefined : copyRecord(envelope.data.record);
    return {
      kind: 'generation',
      expectation: { kind: 'generation', generation: envelope.data.generation },
      generation: envelope.data.generation,
      ...(record === undefined ? {} : { record }),
    };
  }

  const legacy = parseRecord(value);
  if (legacy !== undefined) {
    return {
      kind: 'legacy',
      expectation: { kind: 'legacy', fingerprint: legacyRecordFingerprint(legacy) },
      record: legacy,
    };
  }

  return { kind: 'invalid' };
}

function currentMatchesExpectation(
  actual: ParsedCurrentSessionValue,
  expected: CurrentSessionExpectation,
): boolean {
  if (actual.kind === 'invalid' || expected.kind === 'unavailable' || actual.kind !== expected.kind) {
    return false;
  }

  if (actual.kind === 'empty') {
    return true;
  }
  if (actual.kind === 'generation' && expected.kind === 'generation') {
    return actual.generation === expected.generation;
  }
  return actual.kind === 'legacy'
    && expected.kind === 'legacy'
    && actual.expectation.fingerprint === expected.fingerprint;
}

function nextGenerationFor(current: ParsedCurrentSessionValue): number {
  return current.kind === 'generation' ? current.generation + 1 : 1;
}

/** Stable only for structurally valid raw v1 records; it is never persisted. */
function legacyRecordFingerprint(record: GameSessionRecord): string {
  return JSON.stringify(copyRecord(record));
}

function compareByMostRecentUpdate(left: GameSessionRecord, right: GameSessionRecord): number {
  return right.updatedAtMs - left.updatedAtMs || left.gameId.localeCompare(right.gameId);
}

function copyRecord(record: GameSessionRecord): GameSessionRecord {
  return Object.freeze({
    schemaVersion: record.schemaVersion,
    gameId: record.gameId,
    createdAtMs: record.createdAtMs,
    updatedAtMs: record.updatedAtMs,
    gameEpoch: record.gameEpoch,
    initialFen: record.initialFen,
    acceptedUciMoves: Object.freeze([...record.acceptedUciMoves]),
    acceptedMoves: Object.freeze(record.acceptedMoves.map((move) => Object.freeze({
      uci: move.uci,
      san: move.san,
      actor: move.actor,
      committedAtMs: move.committedAtMs,
      ...(move.clockBefore ? { clockBefore: copyClock(move.clockBefore) } : {}),
    }))),
    derivedFen: record.derivedFen,
    result: Object.freeze({ ...record.result }),
    mode: record.mode.kind === 'hot-seat'
      ? Object.freeze({ kind: 'hot-seat' as const })
      : Object.freeze({
          kind: 'computer' as const,
          humanColor: record.mode.humanColor,
          ...(record.mode.humanColorPreference === undefined
            ? {}
            : { humanColorPreference: record.mode.humanColorPreference }),
          learnMode: record.mode.learnMode,
          ai: Object.freeze({ ...record.mode.ai }),
        }),
    ...(record.clock ? { clock: copyClock(record.clock) } : {}),
    ...(record.clockTimelineAnchors === undefined
      ? {}
      : {
          clockTimelineAnchors: Object.freeze(record.clockTimelineAnchors.map((anchor) => Object.freeze({
            ply: anchor.ply,
            atMs: anchor.atMs,
            clock: copyClock(anchor.clock),
          }))),
        }),
    ai: Object.freeze({
      nextRequestId: record.ai.nextRequestId,
      ...(record.ai.pending ? { pending: Object.freeze({ ...record.ai.pending }) } : {}),
      ...(record.ai.lastFailure ? { lastFailure: Object.freeze({ ...record.ai.lastFailure }) } : {}),
    }),
  });
}

function copyClock(clock: GameSessionRecord['clock'] & {}): NonNullable<GameSessionRecord['clock']> {
  return Object.freeze({ ...clock });
}

function toStorageFailure(
  operation: 'read' | 'write',
  cause: unknown,
): GameSessionPersistenceFailure {
  const name = errorName(cause);
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
    return { kind: 'quota', operation, cause };
  }
  if (name === 'SecurityError') {
    return { kind: 'security', operation, cause };
  }
  return operation === 'read'
    ? { kind: 'read-failed', cause }
    : { kind: 'write-failed', cause };
}

function errorName(cause: unknown): string | undefined {
  try {
    return cause !== null && typeof cause === 'object' && 'name' in cause && typeof cause.name === 'string'
      ? cause.name
      : undefined;
  } catch {
    return undefined;
  }
}
